import type {
  EnvironmentId,
  PullRequestListInput,
  PullRequestListFilters,
} from "@cmmd-forge/contracts";
import { keepPreviousData } from "@tanstack/react-query";
import { useEffect } from "react";

import { closePrimarySidebar } from "../../components/sidebar/primarySidebarBridge";
import { usePrimaryEnvironmentDescriptor } from "../../environments/primary";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import {
  COMMAND_AUTHORITY_SCOPE,
  pullRequestListInfiniteQueryOptions,
} from "../../lib/pullRequestReactQuery";
import { PULL_REQUESTS_PANEL_KEY } from "../../pullRequest/rightPanel";
import { useRightPanelStore } from "../../rightPanelStore";
import type { PullRequestRightPanelSurface, ThreadRightPanelState } from "../../rightPanelSurface";
import { PullRequestInbox } from "./PullRequestsPage.views";
import { PullRequestsHeader, type CapableEnvironment } from "./PullRequestsPage.header";
import {
  activeSurfaceMatchesPullRequestRow,
  aggregatePullRequestQueries,
  isDefaultPullRequestInboxSearch,
  pullRequestInboxState,
  rowMatchesPullRequestSearch,
} from "./PullRequestsPage.helpers";
import { PullRequestsPanelLayout } from "./PullRequestsPanelLayout";
import { sortPullRequestInboxRows } from "./pullRequestListSort.logic";
import {
  findDeepLinkedPullRequest,
  hasActivePullRequestFilters,
  parsePullRequestQualifierQuery,
  type ParsedPullRequestQuery,
  type PullRequestsSearch,
} from "./pullRequests.logic";
import {
  usePersistPullRequestWarmSnapshot,
  usePullRequestWarmSnapshot,
} from "./usePullRequestWarmSnapshot";
import { usePullRequestInfiniteQueries } from "./usePullRequestInfiniteQueries";
import { usePullRequestSavedEnvironmentConnections } from "./usePullRequestSavedEnvironmentConnections";

interface PullRequestsPageProps {
  readonly search: PullRequestsSearch;
  readonly onSearchChange: (search: PullRequestsSearch) => void;
}

export const COMMAND_PULL_REQUEST_QUERY_ENVIRONMENTS: ReadonlyArray<CapableEnvironment> = [
  { environmentId: COMMAND_AUTHORITY_SCOPE, label: "Command" },
];

export function resetPullRequestsRoute(input: {
  readonly closePanel: () => void;
  readonly navigate: (search: PullRequestsSearch) => void;
}): void {
  input.closePanel();
  input.navigate({ state: "open" });
}

export function synchronizePullRequestWorkspaceLayout(
  panelOpen: boolean,
  closeSidebar: () => void = closePrimarySidebar,
): void {
  if (panelOpen) {
    closeSidebar();
  }
}

function useCapablePullRequestEnvironments(): ReadonlyArray<CapableEnvironment> {
  const primary = usePrimaryEnvironmentDescriptor();
  const savedRuntimeById = useSavedEnvironmentRuntimeStore((state) => state.byId);
  return [
    ...(primary?.capabilities.pullRequests === true
      ? [{ environmentId: primary.environmentId, label: primary.label }]
      : []),
    ...Object.entries(savedRuntimeById).flatMap(([environmentId, runtime]) =>
      runtime.connectionState === "connected" &&
      runtime.descriptor?.capabilities.pullRequests === true
        ? [
            {
              environmentId: environmentId as EnvironmentId,
              label: runtime.descriptor.label,
            },
          ]
        : [],
    ),
  ].toSorted((left, right) => left.label.localeCompare(right.label));
}

function listInput(
  search: PullRequestsSearch,
  parsedQuery: ParsedPullRequestQuery,
): PullRequestListInput {
  const filters = pullRequestFilters(search, parsedQuery.filters);
  return {
    state: search.state,
    ...optionalField("involvement", search.involvement),
    ...optionalField("host", search.host),
    ...optionalField("projectId", search.projectId),
    ...optionalField("query", parsedQuery.text || undefined),
    ...optionalField("filters", Object.keys(filters).length > 0 ? filters : undefined),
    limit: 99,
  };
}

function pullRequestFilters(
  search: PullRequestsSearch,
  parsedFilters: PullRequestListFilters,
): PullRequestListFilters {
  return Object.fromEntries(
    Object.entries({
      ...parsedFilters,
      draft: search.draft,
      review: search.review,
      checks: search.checks,
    }).filter(([, value]) => value !== undefined),
  ) as PullRequestListFilters;
}

function optionalField<Key extends string, Value>(
  key: Key,
  value: Value | undefined,
): Readonly<Partial<Record<Key, Value>>> {
  return (value === undefined ? {} : { [key]: value }) as Partial<Record<Key, Value>>;
}

function activePullRequestSurface(
  panelState: ThreadRightPanelState | null,
): PullRequestRightPanelSurface | null {
  if (panelState === null) return null;
  return (
    panelState.surfaces.find(
      (surface): surface is PullRequestRightPanelSurface =>
        surface.id === panelState.activeSurfaceId && surface.kind === "pull-request",
    ) ?? null
  );
}

function panelLayoutState(panelState: ThreadRightPanelState | null) {
  if (panelState === null) {
    return {
      open: false,
      maximized: false,
      surfaces: [],
      activeSurfaceId: null,
    } as const;
  }
  return {
    open: panelState.isOpen,
    maximized: panelState.isMaximized ?? false,
    surfaces: panelState.surfaces,
    activeSurfaceId: panelState.activeSurfaceId,
  };
}

export function PullRequestsPage({ search, onSearchChange }: PullRequestsPageProps) {
  usePullRequestSavedEnvironmentConnections();
  const environments = useCapablePullRequestEnvironments();
  // Command is available through the owner JWT independently of Forge runtimes. This synthetic
  // scope is query/cache identity only; execution environments are resolved later by the handoff.
  const queryEnvironments = COMMAND_PULL_REQUEST_QUERY_ENVIRONMENTS;
  // Parsed once and shared: the request carries the qualifier-stripped text, and the sort's
  // search stand-down reads the same free text, not the raw query whose qualifiers would
  // otherwise keep "Ready to merge" and "Blocked" switched off for a qualifier-only search.
  const parsedQuery = parsePullRequestQualifierQuery(search.query ?? "");
  const input = listInput(search, parsedQuery);
  const canUseWarmSnapshot = isDefaultPullRequestInboxSearch(search);
  const warmSnapshot = usePullRequestWarmSnapshot({
    environmentIds: queryEnvironments.map((environment) => environment.environmentId),
    enabled: canUseWarmSnapshot,
  });
  const queryOptions = queryEnvironments.map((environment) => {
    const options = pullRequestListInfiniteQueryOptions({
      environmentId: environment.environmentId,
      input,
    });
    const initialData = warmSnapshot.initialDataFor(
      environment.environmentId,
      options.initialPageParam,
    );
    return {
      ...options,
      placeholderData: keepPreviousData,
      ...(initialData === undefined ? {} : { initialData, initialDataUpdatedAt: 0 }),
    };
  });
  const queries = usePullRequestInfiniteQueries(queryOptions);
  usePersistPullRequestWarmSnapshot({
    environmentIds: queryEnvironments.map((environment) => environment.environmentId),
    enabled: canUseWarmSnapshot,
    results: queries,
  });
  const aggregate = aggregatePullRequestQueries(queries, queryEnvironments);
  const panelState = useRightPanelStore(
    (state) => state.byThreadKey[PULL_REQUESTS_PANEL_KEY] ?? null,
  );
  const openPullRequest = useRightPanelStore((state) => state.openPullRequest);
  const activateSurface = useRightPanelStore((state) => state.activateSurface);
  const closeSurface = useRightPanelStore((state) => state.closeSurface);
  const closePanel = useRightPanelStore((state) => state.close);
  const toggleMaximized = useRightPanelStore((state) => state.toggleMaximized);
  const activeSurface = activePullRequestSurface(panelState);
  const activeRowKey = aggregate.rows.find((row) =>
    activeSurfaceMatchesPullRequestRow(activeSurface, row),
  )?.rowKey;
  const panelLayout = panelLayoutState(panelState);
  const groups = aggregate.groups.flatMap((group) => {
    const rows = sortPullRequestInboxRows(
      group.rows.filter((row) => rowMatchesPullRequestSearch(row, search)),
      search.sort,
      parsedQuery.text,
    );
    return rows.length === 0 ? [] : [{ ...group, rows }];
  });
  const selectedRow = findDeepLinkedPullRequest(aggregate.rows, search);
  const hostOptions = [...new Set(aggregate.rows.map((row) => row.host))].toSorted();
  const projectOptions = aggregate.rows
    .map((row) => ({ id: row.projectId, label: row.projectTitle ?? row.repository }))
    .filter(
      (option, index, options) =>
        options.findIndex((candidate) => candidate.id === option.id) === index,
    )
    .toSorted((left, right) => left.label.localeCompare(right.label));

  useEffect(() => {
    if (selectedRow === undefined || activeSurfaceMatchesPullRequestRow(activeSurface, selectedRow))
      return;
    openPullRequest(PULL_REQUESTS_PANEL_KEY, {
      provider: selectedRow.provider,
      environmentId: selectedRow.environmentId,
      repositoryLinkId:
        selectedRow.projectRepositoryId === undefined
          ? null
          : String(selectedRow.projectRepositoryId),
      projectId: selectedRow.projectId,
      host: selectedRow.host,
      repository: selectedRow.repository,
      number: selectedRow.number,
      title: selectedRow.title,
    });
  }, [activeSurface, openPullRequest, selectedRow]);

  useEffect(() => {
    synchronizePullRequestWorkspaceLayout(panelLayout.open);
  }, [panelLayout.open]);

  const closePullRequestPanel = () => closePanel(PULL_REQUESTS_PANEL_KEY);

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-1 bg-background text-foreground">
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="scrollbar-gutter-both min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-5 pt-6 pb-12 sm:px-6">
            <PullRequestsHeader
              search={search}
              onSearchChange={onSearchChange}
              hostOptions={hostOptions}
              projectOptions={projectOptions}
              unavailableErrors={aggregate.errors}
              isRefreshing={queries.some((query) => query.isFetching && query.data !== undefined)}
              onReset={() =>
                resetPullRequestsRoute({
                  closePanel: closePullRequestPanel,
                  navigate: onSearchChange,
                })
              }
              onRefresh={() => {
                warmSnapshot.invalidate();
                queries.forEach((query) => void query.refetch());
              }}
            />
            <PullRequestInbox
              groups={groups}
              activeRowKey={activeRowKey}
              environmentLabels={
                new Map(
                  [...queryEnvironments, ...environments].map((environment) => [
                    environment.environmentId,
                    environment.label,
                  ]),
                )
              }
              state={pullRequestInboxState({
                environmentCount: queryEnvironments.length,
                queryCount: queries.length,
                aggregateState: aggregate.state,
              })}
              errors={aggregate.errors}
              hasMore={queries.some((query) => query.hasNextPage)}
              isFetchingMore={queries.some((query) => query.isFetchingNextPage)}
              isRefreshing={queries.some((query) => query.isFetching && query.data !== undefined)}
              hasActiveFilters={hasActivePullRequestFilters(search)}
              hasSearchQuery={search.query !== undefined}
              onLoadMore={() => {
                queries.forEach((query) => {
                  if (query.hasNextPage) void query.fetchNextPage();
                });
              }}
              onRetry={() => queries.forEach((query) => void query.refetch())}
              onClearFilters={() =>
                resetPullRequestsRoute({
                  closePanel: closePullRequestPanel,
                  navigate: onSearchChange,
                })
              }
              onOpen={(row) => {
                openPullRequest(PULL_REQUESTS_PANEL_KEY, {
                  provider: row.provider,
                  environmentId: row.environmentId,
                  repositoryLinkId:
                    row.projectRepositoryId === undefined ? null : String(row.projectRepositoryId),
                  projectId: row.projectId,
                  host: row.host,
                  repository: row.repository,
                  number: row.number,
                  title: row.title,
                });
                onSearchChange({
                  ...search,
                  environmentId: row.environmentId,
                  provider: row.provider,
                  host: row.host,
                  repository: row.repository,
                  number: row.number,
                });
              }}
            />
          </div>
        </div>
      </main>
      <PullRequestsPanelLayout
        open={panelLayout.open}
        maximized={panelLayout.maximized}
        surfaces={panelLayout.surfaces}
        activeSurface={activeSurface}
        activeSurfaceId={panelLayout.activeSurfaceId}
        onSelectSurface={(surfaceId) => activateSurface(PULL_REQUESTS_PANEL_KEY, surfaceId)}
        onCloseSurface={(surfaceId) => closeSurface(PULL_REQUESTS_PANEL_KEY, surfaceId)}
        onToggleMaximized={() => toggleMaximized(PULL_REQUESTS_PANEL_KEY)}
        onClose={closePullRequestPanel}
      />
    </div>
  );
}
